import type { OrganizationSubscriptionSummary } from '@/lib/utils/subscription-access'
import { organizationsApi } from '@/lib/api/organizations.api'
import { useAuth } from '@/store/auth.store'
import { useQuery } from '@tanstack/react-query'

import type { Organization } from '@scrambl/shared'

import { queryKeys } from '../query-keys'

// Extended Organization type with userMembership
export interface ExtendedOrganization extends Organization {
  userMembership?: {
    id: string
    role: string
    status: string
  } | null
  clientPortalAccesses?: Array<{
    id: string
    entityId: string
    entityName?: string | null
    /** Portal contact email — disambiguates same-named portals in the picker. */
    entityEmail?: string | null
    status: string
  }>
  logoUrl?: string | null
  /**
   * Billing state of the organization, as returned by
   * `GET /organizations/with-logos`. `null` means the organization has no
   * subscription row (personal accounts always do); `undefined` means the
   * response did not carry the field at all, so callers must not infer
   * anything from it.
   */
  subscriptionSummary?: OrganizationSubscriptionSummary | null
  /** Owner contact, so members can be told who to ask about billing. */
  owner?: {
    name?: string | null
    email?: string | null
  } | null
}

interface OrganizationsWithLogosResponse {
  data: ExtendedOrganization[]
  meta: {
    total: number
    skip: number
    take: number
  }
}

/**
 * Hook to fetch all organizations for the authenticated user with logos
 * Includes active direct memberships and active portal access
 */
export function useOrganizationsQuery(options?: {
  forceRefresh?: boolean
  enabled?: boolean
}) {
  const { isAuthenticated, isAuthReady } = useAuth()

  return useQuery<ExtendedOrganization[]>({
    queryKey: queryKeys.organizations.list({ withLogos: true }),
    queryFn: async () => {
      const response = await organizationsApi.getAllWithLogos({
        forceRefresh: options?.forceRefresh,
      })

      // Pending, missing and unknown statuses are not active memberships.
      const organizations = response.data.filter(org => {
        const membershipStatus = org.userMembership?.status
        const hasActiveMembership = membershipStatus === 'ACTIVE'
        const hasActivePortalAccess = !!org.clientPortalAccesses?.some(
          access => access.status === 'ACTIVE',
        )

        return hasActiveMembership || hasActivePortalAccess
      })

      return organizations
    },
    enabled: options?.enabled !== false && isAuthReady && isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes - organizations don't change often
    gcTime: 24 * 60 * 60 * 1000, // 24 hours - keep in cache
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnReconnect: true, // Refetch on reconnect
  })
}

/**
 * Hook to fetch a single organization by ID
 */
export function useOrganizationQuery(
  id: string | undefined,
  options?: {
    enabled?: boolean
  },
) {
  const { isAuthenticated, isAuthReady } = useAuth()

  return useQuery<ExtendedOrganization>({
    queryKey: queryKeys.organizations.detail(id || ''),
    queryFn: () => organizationsApi.getById(id!),
    enabled:
      options?.enabled !== false && !!id && isAuthReady && isAuthenticated,
    staleTime: 5 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  })
}

/**
 * Hook to fetch an organization by slug
 */
export function useOrganizationBySlugQuery(
  slug: string | undefined,
  options?: {
    enabled?: boolean
  },
) {
  const { isAuthenticated, isAuthReady } = useAuth()

  return useQuery<ExtendedOrganization>({
    queryKey: [...queryKeys.organizations.all, 'by-slug', slug || ''],
    queryFn: () => organizationsApi.getBySlug(slug!),
    enabled:
      options?.enabled !== false && !!slug && isAuthReady && isAuthenticated,
    staleTime: 5 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  })
}

/**
 * Hook to check if an organization name is available
 */
export function useOrganizationNameAvailability(name: string, enabled = false) {
  return useQuery<{ available: boolean; message?: string }>({
    queryKey: queryKeys.organizations.nameAvailability(name),
    queryFn: () => organizationsApi.checkNameAvailability(name),
    enabled: enabled && name.length > 0,
    staleTime: Infinity, // Availability checks can be cached
    gcTime: 5 * 60 * 1000,
  })
}

/**
 * Hook to check if an organization slug is available
 */
export function useOrganizationSlugAvailability(slug: string, enabled = false) {
  return useQuery<{ available: boolean; message?: string }>({
    queryKey: queryKeys.organizations.slugAvailability(slug),
    queryFn: () => organizationsApi.checkSlugAvailability(slug),
    enabled: enabled && slug.length > 0,
    staleTime: Infinity, // Availability checks can be cached
    gcTime: 5 * 60 * 1000,
  })
}
